import { useEffect, useId, useLayoutEffect, useRef, useState, type MutableRefObject, type ReactElement } from 'react';
import type { InputState, RoomSnapshot } from '../shared/types';
import { listenForAudioUnlock, setAudioEnabled } from './audio';
import { HearthhopRenderer } from './renderer';
import { LANTERN_COLORS } from './art/lantern';
import { describeHazard } from './art/hazards';
import { dangerZones } from './art/camera';
import { DANGER_CUE_RANGE } from './art/annotations';
import { getPlatforms } from '../shared/simulation';

export interface GameCanvasProps {
  snapshot: RoomSnapshot | null;
  playerId: string | null;
  input: MutableRefObject<InputState>;
  sound: boolean;
  reducedMotion?: boolean;
  mode?: 'game' | 'hero';
  /** Reveal the authored in-world sign cards, alongside the shell's written nudge. */
  showHints?: boolean;
}

/** Canvases at least this wide (CSS px) default to the whole-trail view for parties of WIDE_OVERVIEW_PARTY or more. */
export const WIDE_OVERVIEW_WIDTH = 1100;
export const WIDE_OVERVIEW_PARTY = 4;
/** The player's explicit view choice persists for the tab's session (sessionStorage), across trails and remounts. */
const VIEW_STORAGE_KEY = 'hearthhop.view';

function storedView(): 'auto' | 'follow' | 'overview' {
  try {
    const value = typeof window !== 'undefined' ? window.sessionStorage.getItem(VIEW_STORAGE_KEY) : null;
    return value === 'follow' || value === 'overview' ? value : 'auto';
  } catch { return 'auto'; }
}

/** Stable Canvas2D presentation. The shell's input ref is observed, never owned or modified. */
export function GameCanvas(props: GameCanvasProps): ReactElement {
  const { snapshot, playerId, input, sound, mode = 'game', showHints = false } = props;
  const canvas = useRef<HTMLCanvasElement>(null);
  const renderer = useRef<HearthhopRenderer | null>(null);
  const stage = useRef<HTMLDivElement>(null);
  const viewDescriptionId = useId();
  const [view, setView] = useState<'auto' | 'follow' | 'overview'>(() => mode === 'game' ? storedView() : 'auto');
  const [stageWidth, setStageWidth] = useState(0);
  const [resetNotice, setResetNotice] = useState<string | null>(null);
  const announcedReset = useRef<number | null>(null);
  const resetNoticeTimer = useRef<number | undefined>(undefined);
  const [systemReducedMotion, setSystemReducedMotion] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const reducedMotion = props.reducedMotion ?? systemReducedMotion;
  // A completion this mounted view WATCHED happen (it saw the same attempt incomplete)
  // keeps the close team view for the shell's celebration hold: the team in the lit door,
  // confetti and poses stay full-size. A view mounted onto an already-complete trail
  // (the result postcard) shows the whole trail as before.
  const world = snapshot?.world;
  const witnessed = useRef<{ code: string; level: number; epoch: number } | null>(null);
  if (snapshot && world && !world.complete) witnessed.current = { code: snapshot.code, level: world.levelIndex, epoch: world.epoch };
  const liveCompletion = !!snapshot && !!world?.complete && witnessed.current?.code === snapshot.code
    && witnessed.current.level === world.levelIndex && witnessed.current.epoch === world.epoch;
  // Default view: a wide canvas with a party of four or more shows the whole trail (every helper and
  // the whole tower row stay visible); pairs, trios and narrower canvases keep the close Team view.
  // An explicit toggle always wins and persists for the session.
  const wideParty = stageWidth >= WIDE_OVERVIEW_WIDTH && (world?.partySize ?? snapshot?.players.length ?? 0) >= WIDE_OVERVIEW_PARTY;
  const follow = view === 'follow' || (view === 'auto' && !wideParty && (!world?.complete || liveCompletion));
  const renderView = follow ? 'follow' as const : 'overview' as const;
  const latest = useRef({ ...props, reducedMotion, view: renderView });
  latest.current = { ...props, reducedMotion, view: renderView };

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const change = () => setSystemReducedMotion(query.matches);
    query.addEventListener('change', change);
    return () => query.removeEventListener('change', change);
  }, []);

  useLayoutEffect(() => {
    const element = stage.current;
    if (!element || mode !== 'game') return;
    const measure = () => setStageWidth(element.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver === 'undefined') { window.addEventListener('resize', measure, { passive: true }); return () => window.removeEventListener('resize', measure); }
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [mode]);

  useLayoutEffect(() => {
    if (!canvas.current) return;
    const instance = new HearthhopRenderer(canvas.current, latest.current);
    renderer.current = instance;
    return () => {
      instance.destroy();
      if (renderer.current === instance) renderer.current = null;
    };
  }, []);

  useLayoutEffect(() => {
    renderer.current?.update({ snapshot, playerId, input, sound, reducedMotion, mode, view: renderView, showHints });
    // The renderer attributes a received reset (the engine's event is anonymous); the live region
    // repeats its chip once per reset so screen-reader players hear who fell too.
    const reset = renderer.current?.activeReset ?? null;
    if (reset && reset.eventId !== announcedReset.current) {
      announcedReset.current = reset.eventId;
      setResetNotice(reset.label);
      window.clearTimeout(resetNoticeTimer.current);
      resetNoticeTimer.current = window.setTimeout(() => setResetNotice(null), 2500);
    }
  }, [snapshot, playerId, input, sound, reducedMotion, mode, renderView, showHints]);
  useEffect(() => () => window.clearTimeout(resetNoticeTimer.current), []);

  useEffect(() => listenForAudioUnlock(), []);
  useEffect(() => { setAudioEnabled(sound); }, [sound]);

  const self = snapshot?.players.find(player => player.id === playerId);
  const body = snapshot?.world?.players.find(player => player.id === playerId);
  const missingKey = snapshot?.level?.keys.find(key => !snapshot.world?.collectedKeys.includes(key.id));
  const goal = missingKey ?? snapshot?.level?.exit;
  const direction = body && goal ? goal.x + goal.w / 2 > body.x + body.w ? 'to your right' : goal.x + goal.w / 2 < body.x ? 'to your left' : 'at your position' : '';
  const elevation = body && goal && goal.y + goal.h < body.y ? ', higher up' : '';
  const waiting = snapshot?.world?.players.filter(player => player.connected && !player.entered).length ?? 0;
  const plateStatus = snapshot?.level?.switches.map((plate, index) => {
    const state = snapshot.world?.switches.find(candidate => candidate.id === plate.id);
    return `Plate ${String.fromCharCode(65 + index)}: ${state?.count ?? 0} of ${state?.required ?? plate.required}${state?.latched ? ', latched' : state?.active ? ', active' : ''}.`;
  }).join(' ') ?? '';
  const hazardStatus = snapshot?.world && snapshot.level ? snapshot.level.hazards.filter(hazard => hazard.cycle || hazard.disabledBy?.length).map(hazard => describeHazard(hazard, snapshot.level!, snapshot.world!).accessible).join(' ') : '';
  // Deadly surfaces the local courier could fall into nearby are announced with their side.
  const dangerStatus = snapshot?.world && snapshot.level && body && !body.entered
    ? dangerZones(snapshot.level, getPlatforms(snapshot.level, snapshot.world), snapshot.world, body, DANGER_CUE_RANGE)
      .map(zone => `Deadly ${zone.kind} ${zone.side === 'over' ? 'directly below you' : `${Math.round(zone.distance)} pixels to your ${zone.side}`}.`).join(' ')
    : '';
  const retries = snapshot?.world ? `Room retries so far: ${snapshot.world.deaths}.` : '';
  const viewDescription = snapshot?.world ? `${resetNotice ? `${resetNotice} ` : ''}${follow ? world?.complete ? 'Celebrating at the door' : body?.entered ? 'Watching the remaining team' : 'Close team view' : 'Whole-trail overview'}. ${missingKey ? 'Shared key' : 'Exit'} ${direction}${elevation}. ${waiting} ${waiting === 1 ? 'friend still needs' : 'friends still need'} to reach home. ${plateStatus} ${hazardStatus} ${dangerStatus} ${retries}` : 'Waiting for the trail state.';
  const changeView = (next: 'follow' | 'overview') => {
    setView(next);
    try { window.sessionStorage.setItem(VIEW_STORAGE_KEY, next); } catch { /* private mode: the choice still holds while mounted */ }
    stage.current?.closest<HTMLElement>('.world-frame')?.focus({ preventScroll: true });
  };
  const description = mode === 'hero'
    ? 'Original woodland illustration: five little numbered lantern friends share a shoulder, cross a wooden bridge, and welcome each other home. Decorative preview.'
    : snapshot?.level
      ? `${snapshot.level.title}. A cooperative woodland trail.${self ? ` You are lantern ${self.color + 1}, ${self.name}.` : ''} ${follow ? 'Close team view keeps nearby partners and their supports together. Numbered edge arrows show distant teammates and objectives. Use Whole trail to see the entire level.' : 'Whole trail overview. Use Team view to return to the physical action.'} Numbered lanterns, a shared amber key, counted plates, and a welcoming exit.`
      : 'Waiting for the authoritative woodland trail.';

  return (
    <div ref={stage} data-hearthhop-stage={mode} style={{ position: 'relative', width: '100%', height: '100%', isolation: 'isolate' }}>
      <style>{`
        @media (max-width: 700px) and (orientation: portrait) {
          .world-frame:has(> [data-hearthhop-stage="game"]) { aspect-ratio: auto; height: clamp(350px, 46dvh, 440px); }
        }
        @media (max-height: 520px) and (orientation: landscape) {
          .world-frame:has(> [data-hearthhop-stage="game"]) { width: 100%; aspect-ratio: auto; height: calc(100dvh - 94px); }
        }
      `}</style>
      <canvas
      ref={canvas}
      className={`game-canvas game-canvas--${mode}`}
      data-hearthhop-canvas={mode}
      role="img"
      aria-label={description}
      aria-describedby={mode === 'game' ? viewDescriptionId : undefined}
      style={{ display: 'block', width: '100%', height: '100%' }}
    >
      {description} This game uses Canvas2D; a current browser is required to display the trail.
      </canvas>
      {mode === 'game' && <>
        <p id={viewDescriptionId} role="status" aria-live="polite" style={{ position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clipPath: 'inset(50%)', whiteSpace: 'nowrap' }}>{viewDescription}</p>
        <div role="group" aria-label="Your lantern and camera view" onPointerDown={event => event.stopPropagation()}
          style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '0 9px', boxSizing: 'border-box', background: '#fff5df', borderTop: '1px solid #bcc6a7', color: '#293f39', font: '700 12px system-ui, sans-serif' }}>
          <span data-local-lantern={self ? self.color + 1 : undefined} aria-label={self ? `You are lantern ${self.color + 1}, ${self.name}` : 'Spectating the trail'} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}>
            <span style={{ display: 'grid', placeItems: 'center', width: 23, height: 23, border: '2px solid #293f39', borderRadius: '50%', color: '#293f39', background: self ? LANTERN_COLORS[self.color % 8] : '#e8e3cf', fontWeight: 900 }}>{self ? self.color + 1 : '•'}</span>
            {self ? 'YOU' : 'WATCHING'}
            <span style={{ fontWeight: 500, color: '#51644f' }}>{follow ? '· Team view' : '· Overview'}</span>
          </span>
          <button type="button" aria-label={follow ? 'Show whole trail overview' : 'Follow my lantern closely'} aria-pressed={!follow}
            onClick={event => { event.stopPropagation(); changeView(follow ? 'overview' : 'follow'); }}
            style={{ minHeight: 30, padding: '4px 10px', border: '1px solid #6f8365', borderRadius: 6, background: '#eef0db', color: '#293f39', font: '700 12px system-ui, sans-serif', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            {follow ? '↔ Whole trail' : '◎ Team view'}
          </button>
        </div>
      </>}
    </div>
  );
}
